// client/src/views/Interactive_parts/MapControls.js
import React, { useState, useEffect, useRef } from 'react';
import { Eye, Pin, Code2, Pencil, Maximize2, ImagePlus, Shield, Paintbrush } from 'lucide-react';

function MapControls({
  isMobile,
  toolboxBottom,
  showFound,
  setShowFound,
  placingPin,
  setPlacingPin,
  isMapOwnerOrFounder,
  onSeoEditClick,
  onShowOverlayModal,
  onShowEmbedPopup,
  onResetView,
  adsEnabled,
  rememberUiState,
  onToggleRememberUiState,
  onWipePreferences,
}) {
  const containerStyle = isMobile
    ? {
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1600,
        bottom: `${toolboxBottom}px`,
        background: '#222f',
        borderRadius: 16,
        padding: '3px 14px',
        boxShadow: '0 2px 14px #000b',
        display: 'flex',
        gap: 6,
      }
    : undefined;

  const btnStyle = {
    width: 32,
    height: 32,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const iconSize = 17;

  const [privacyMenuOpen, setPrivacyMenuOpen] = useState(false);
  const privacyBtnRef = useRef(null);
  const privacyMenuRef = useRef(null);

  const [cmpReady, setCmpReady] = useState(false);
  const [stylerReady, setStylerReady] = useState(false);

  const computeCmpReady = () => {
    try {
      if (typeof window === 'undefined') return false;
      return typeof window.__cmp === 'function';
    } catch {
      return false;
    }
  };

  const computeStylerReady = () => {
    try {
      if (typeof window === 'undefined') return false;
      return typeof window.__paOpenStyler === 'function';
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const update = () => setCmpReady(computeCmpReady());

    update();

    const onNitroLoaded = () => update();

    try {
      document.addEventListener('nitroAds.loaded', onNitroLoaded);
    } catch {}

    const t = setInterval(() => update(), 1200);

    return () => {
      clearInterval(t);
      try {
        document.removeEventListener('nitroAds.loaded', onNitroLoaded);
      } catch {}
    };
  }, []);

  useEffect(() => {
    const update = () => setStylerReady(computeStylerReady());

    update();

    const t = setInterval(() => update(), 1200);

    return () => {
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    if (!privacyMenuOpen) return;

    const onDown = (e) => {
      const btn = privacyBtnRef.current;
      const menu = privacyMenuRef.current;

      if (btn && btn.contains(e.target)) return;
      if (menu && menu.contains(e.target)) return;

      setPrivacyMenuOpen(false);
    };

    const onKey = (e) => {
      if (e.key === 'Escape') setPrivacyMenuOpen(false);
    };

    window.addEventListener('mousedown', onDown);
    window.addEventListener('touchstart', onDown, { passive: true });
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('touchstart', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [privacyMenuOpen]);

  const tryOpenCmpModal = () => {
    try {
      if (typeof window === 'undefined') return false;
      if (typeof window.__cmp === 'function') {
        window.__cmp('showModal');
        return true;
      }
    } catch {}
    return false;
  };

  const handleOpenPrivacySettings = () => {
    if (!adsEnabled) return;

    const opened = tryOpenCmpModal();

    if (!opened) {
      let done = false;

      const cleanup = () => {
        if (done) return;
        done = true;
        try {
          document.removeEventListener('nitroAds.loaded', onNitroLoaded);
        } catch {}
      };

      const onNitroLoaded = () => {
        const ok = tryOpenCmpModal();
        setCmpReady(computeCmpReady());
        cleanup();

        if (!ok) {
          try {
            console.warn('[privacy] Consent UI still not available after nitroAds.loaded. Likely blocked or not in GDPR zone.');
          } catch {}
        }
      };

      try {
        document.addEventListener('nitroAds.loaded', onNitroLoaded);
      } catch {}

      setTimeout(() => {
        cleanup();
        setCmpReady(computeCmpReady());
        try {
          console.warn('[privacy] Consent UI not available yet (CMP not loaded).');
        } catch {}
      }, 4500);
    }

    setPrivacyMenuOpen(false);
  };

  const handleToggleRemember = () => {
    if (typeof onToggleRememberUiState === 'function') {
      onToggleRememberUiState();
    }
  };

  const handleWipePreferences = () => {
    const msg =
      'Wipe local map preferences on this device?\n\n' +
      'This clears things like:\n' +
      '• Found POIs / map progress stored locally\n' +
      '• UI state (open panels, filters, last choices)\n\n' +
      'This does NOT affect your account or other devices.\n\n' +
      'Continue?';

    let ok = false;
    try {
      ok = window.confirm(msg);
    } catch {
      ok = false;
    }
    if (!ok) return;

    if (typeof onWipePreferences === 'function') {
      onWipePreferences();
    }

    setPrivacyMenuOpen(false);
  };

  const handleOpenStyler = () => {
    try {
      if (typeof window !== 'undefined' && typeof window.__paOpenStyler === 'function') {
        window.__paOpenStyler();
      }
    } catch {}
  };

  const rememberChecked = rememberUiState !== false;

  const showConsentLink = !!adsEnabled && !!cmpReady;

  return (
    <div className="map-controls-cluster" style={containerStyle}>
      <button
        className={`map-controls-btn ${showFound ? 'active' : ''}`}
        onClick={() => setShowFound((f) => !f)}
        title={showFound ? 'Hide found POIs' : 'Show found POIs'}
        style={btnStyle}
      >
        <Eye size={iconSize} />
      </button>

      <button
        className={`map-controls-btn ${placingPin ? 'active' : ''}`}
        title="Add Personal Pin"
        onClick={() => setPlacingPin((val) => !val)}
        style={btnStyle}
      >
        <Pin size={iconSize} />
      </button>

      {typeof onShowEmbedPopup === 'function' && (
        <button className="map-controls-btn" title="Embed this map" onClick={onShowEmbedPopup} style={btnStyle}>
          <Code2 size={iconSize} />
        </button>
      )}

      {stylerReady && (
        <button
          className="map-controls-btn"
          title="Edit map styles"
          onClick={handleOpenStyler}
          style={btnStyle}
        >
          <Paintbrush size={iconSize} />
        </button>
      )}

      {isMapOwnerOrFounder && typeof onSeoEditClick === 'function' && (
        <button className="map-controls-btn" title="Edit extra SEO text for this map" onClick={onSeoEditClick} style={btnStyle}>
          <Pencil size={iconSize} />
        </button>
      )}

      {isMapOwnerOrFounder && typeof onShowOverlayModal === 'function' && (
        <button className="map-controls-btn" title="Add image overlay" onClick={onShowOverlayModal} style={btnStyle}>
          <ImagePlus size={iconSize} />
        </button>
      )}

      {typeof onResetView === 'function' && (
        <button className="map-controls-btn" title="Reset view to show full map" onClick={onResetView} style={btnStyle}>
          <Maximize2 size={iconSize} />
        </button>
      )}

      <div style={{ position: 'relative' }}>
        <button
          ref={privacyBtnRef}
          className={`map-controls-btn ${privacyMenuOpen ? 'active' : ''}`}
          title="Privacy options"
          onClick={() => setPrivacyMenuOpen((v) => !v)}
          style={btnStyle}
        >
          <Shield size={iconSize} />
        </button>

        {privacyMenuOpen && (
          <div
            ref={privacyMenuRef}
            style={{
              position: 'absolute',
              right: 0,
              bottom: 40,
              zIndex: 5000,
              background: 'rgba(18,20,26,0.98)',
              border: '1px solid rgba(70,75,95,0.95)',
              boxShadow: '0 10px 26px rgba(0,0,0,0.65)',
              borderRadius: 12,
              padding: 8,
              minWidth: 240,
              color: '#e9ecff',
            }}
          >
            {showConsentLink && (
              <button
                type="button"
                onClick={handleOpenPrivacySettings}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  border: 'none',
                  background: 'transparent',
                  color: '#e9ecff',
                  padding: '10px 10px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                Privacy & cookie settings
              </button>
            )}

            <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '6px 6px' }} />

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 10px',
                borderRadius: 10,
                cursor: 'pointer',
                userSelect: 'none',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <input
                type="checkbox"
                checked={rememberChecked}
                onChange={handleToggleRemember}
                style={{ width: 16, height: 16 }}
              />
              <span style={{ fontWeight: 700 }}>Remember map details</span>
            </label>

            <div style={{ padding: '0 10px 8px 10px', fontSize: 12, opacity: 0.78, lineHeight: 1.35 }}>
              Saves map preferences on this device (e.g. open panels, filters, and your local progress like found POIs).
              If turned off, those won’t be saved between refreshes.
            </div>

            <button
              type="button"
              onClick={handleWipePreferences}
              style={{
                width: '100%',
                textAlign: 'left',
                border: '1px solid rgba(255,120,120,0.35)',
                background: 'rgba(255,90,90,0.10)',
                color: '#ffd9d9',
                padding: '10px 10px',
                borderRadius: 10,
                cursor: 'pointer',
                fontWeight: 700,
                marginTop: 6,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,90,90,0.16)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,90,90,0.10)')}
            >
              Wipe preferences (this device)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MapControls;
